import { type AsyncComponentLoader, defineAsyncComponent, defineComponent, h } from 'vue';
import AsyncErrorMessage from '@/components/common/AsyncLoader/AsyncErrorMessage.vue';
import ElLoadingPanel from '@/components/common/ElLoading/ElLoadingPanel.vue';
import { LOADING_TEXT_VISIBILITY_DELAY_MS } from '@/config';
import { useI18n } from 'vue-i18n';
import useAppLogger from '@/composables/useAppLogger/useAppLogger.ts';

type AsyncComponentLoaderOptions = {
  withLoader?: boolean;
};

export default function useAsyncComponentLoader() {
  const { t } = useI18n();
  const { error: logError } = useAppLogger();

  const getLoadingComponent = (label?: string) =>
    defineComponent(() => () => h(ElLoadingPanel, {
      label: label ?? t('generic.loading.inProgress.label'),
      labelRenderingDelay: LOADING_TEXT_VISIBILITY_DELAY_MS,
    }));

  function getAsyncComponentWithLoader(asyncImport: AsyncComponentLoader, options: AsyncComponentLoaderOptions = {}) {
    const { withLoader = true } = options;
    let retryCount: number = 0;

    return defineAsyncComponent({
      loader: asyncImport,
      loadingComponent: withLoader ? getLoadingComponent() : undefined,
      delay: 200,
      errorComponent: AsyncErrorMessage,
      suspensible: false,
      onError(error, retry, fail, numberOfAttempts) {
        retryCount = numberOfAttempts;
        if (retryCount <= 2) {
          // Tentative de rechargement de la ressource en cas d'échec
          retry();
        } else {
          // journalisation de l'erreur de chargement
          logError("loadAsyncComponent :: Échec du chargement d'un module asynchrone", error);
          fail();
        }
      },
    });
  }

  return {
    getLoadingComponent,
    getAsyncComponentWithLoader,
  };

}
